import React, { useState, useEffect } from "react";
import {
  withStyles,
  IconButton,
  Grid,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Icon from "@jll/icon";
import { recordTypes, scoreValues } from "../../../../API/config";
import { formatDate } from "../../../../Utilities/Functions";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const RecordCheckStatus = (props) => {
  const {
    classes,
    checkType,
    entity = {},
    allocateCheck,
    finalizeScore,
    approveScore,
    hasRecordPermission,
    hasPermission,
    createAMLCheck,
  } = props;
  const { t } = useTranslation();

  const { checkRequests = [], scores = [], processActions = [] } = entity;

  const [checkRequest, setCheckRequest] = useState();
  const [score, setScore] = useState();

  const [canAllocate, setCanAllocate] = useState();
  const [canFinalize, setCanFinalize] = useState();
  const [canApprove, setCanApprove] = useState();
  const [canCreateAml, setCanCreateAml] = useState();

  useEffect(() => {
    const setPermissions = async (checkRequest) => {
      const { permissions: crPermissions = {} } = checkRequest || {};
      const { permissions: recPermissions } = entity;
      const canAllocate = hasRecordPermission(
        crPermissions,
        recordTypes.checkRequest.actions.allocate.name,
      );
      setCanAllocate(canAllocate);

      const canFinalize = hasRecordPermission(
        recPermissions,
        checkType === "KYC"
          ? recordTypes.record.actions.finalizeKycScore.name
          : recordTypes.record.actions.finalizeAmlScore.name,
      );
      setCanFinalize(canFinalize);
      const canApprove = hasRecordPermission(
        recPermissions,
        checkType === "KYC"
          ? recordTypes.record.actions.approveKycScore.name
          : recordTypes.record.actions.approveAmlScore.name,
      );
      setCanApprove(canApprove);

      const canCreateAml = await hasPermission(
        recordTypes.checkRequest.name,
        recordTypes.checkRequest.actions.createAmlCheck.name,
      );
      setCanCreateAml(canCreateAml);
    };

    const checkRequest = checkRequests.find(
      (cr) => cr.check_type === checkType,
    );

    setCheckRequest(checkRequest);
    const score = scores.find((s) => s.check_type === checkType);
    setScore(score);
    setPermissions(checkRequest);
  }, [entity]);

  const renderCanWorkWith = () => {
    const { finalized, approved, rejected, can_work_with } = score || {};
    return (
      <Typography component="span" className={classes.scoreText}>
        {!checkRequest ? (
          t("labels.check.scoring.notInitiated")
        ) : checkRequest.process_flow_state === "Incomplete" ? (
          t("labels.check.scoring.cancelled")
        ) : !score ? (
          t("labels.check.scoring.states.notScored")
        ) : rejected ? (
          t("labels.check.scoring.states.blocked")
        ) : finalized && !approved && !rejected ? (
          t("labels.check.scoring.states.escalated")
        ) : !finalized && !approved && !rejected ? (
          checkRequest.process_flow_state === "Request Sent" ? (
            t("labels.check.scoring.states.requestSent")
          ) : checkRequest.process_flow_state === "Referred To Deal Team" ? (
            t("labels.check.scoring.states.referredToDealTeam")
          ) : (
            t("labels.check.scoring.states.underReview")
          )
        ) : can_work_with ? (
          <>
            {t("labels.check.scoring.states.canWork")}
            <Icon
              className="far fa-check-circle"
              style={{ color: "green", fontSize: 16 }}
            />
          </>
        ) : (
          <>
            {t("labels.check.scoring.states.cantWork")}
            <Icon
              className="far fa-times-circle"
              style={{ color: "red", fontSize: 16 }}
            />
          </>
        )}
      </Typography>
    );
  };

  const renderScore = () => {
    let { accepted_score, guided_score, reason } = score || {};
    let fs = guided_score;
    if (accepted_score != null && accepted_score !== guided_score) {
      fs = accepted_score;
      reason = "";
    }
    const tip = fs !== guided_score ? guided_score : null;

    let className;
    let scoreText;
    if (!fs) {
      className = classes.scoreText;
      scoreText = t("labels.common.na");
    } else if (fs === scoreValues.blocked) {
      className = classes.scoreTextBlocked;
      scoreText = t("labels.check.scoring.blocked");
    } else if (fs === scoreValues.high) {
      className = classes.scoreTextHigh;
      scoreText = t("labels.check.scoring.high");
    } else if (fs === scoreValues.medium) {
      className = classes.scoreTextMedium;
      scoreText = t("labels.check.scoring.medium");
    } else if (fs === scoreValues.low) {
      className = classes.scoreTextLow;
      scoreText = t("labels.check.scoring.low");
    } else {
      className = classes.scoreTextNoScore;
      scoreText = t("labels.check.scoring.noScore");
    }
    return (
      <Grid container justifyContent="center">
        <Grid item>
          {checkRequest && checkRequest.process_flow_state !== "Incomplete" ? (
            <Typography component="span" className={className} title={tip}>
              {scoreText}
            </Typography>
          ) : (
            "-"
          )}
        </Grid>
        <Grid item>
          {reason && (
            <Icon
              title={reason}
              className="far fa-question-circle"
              style={{ marginRight: 5 }}
            />
          )}
        </Grid>
      </Grid>
    );
  };

  const onActionClicked = (action) => {
    if (action === "Allocate") {
      allocateCheck(checkRequest);
    } else if (action === "Finalize") {
      finalizeScore(checkRequest);
    } else if (action === "Approve") {
      approveScore(checkRequest);
    } else if (action === "InitiateAml") {
      createAMLCheck();
    }
  };

  const renderActionIcon = (id, label, title, disabled, iconClass) => {
    return (
      <Typography component="span" className={classes.scoreText} key={id}>
        <Tooltip title={label} placement="bottom">
          <div>
            <IconButton
              onClick={(event) => onActionClicked(title)}
              className={classes.actionIcons}
              style={{ padding: "0px 0px" }}
              disabled={disabled}
              color="primary"
            >
              <Icon className={iconClass} style={{ fontSize: 16 }} />
            </IconButton>
          </div>
        </Tooltip>
      </Typography>
    );
  };

  const renderActions = () => {
    let { accepted_score, guided_score, approved, rejected, expired } =
      score || {};
    let fs = guided_score;
    if (accepted_score != null && accepted_score !== guided_score) {
      fs = accepted_score;
    }

    const { actions = [], process_flow_state } = checkRequest || {};
    const actionIcons = [];

    if (!process_flow_state || process_flow_state === "Incomplete") {
      if (!process_flow_state && checkType === "AML" && canCreateAml) {
        actionIcons.push(
          renderActionIcon(
            0,
            t("labels.check.scoring.actions.initiateAml"),
            "InitiateAml",
            false,
            "fas fa-play",
          ),
        );
      }
      return actionIcons;
    }

    if (
      approved ||
      rejected ||
      expired ||
      (fs && fs.toLowerCase() === "blocked")
    ) {
      return actionIcons;
    }

    if (canAllocate) {
      actionIcons.push(
        renderActionIcon(
          0,
          t("labels.check.scoring.actions.allocate"),
          "Allocate",
          false,
          "far fa-user-tag",
        ),
      );
    }

    for (let pa of processActions) {
      const act = actions.filter((a) => a.label === pa.label);
      const disabled = act.length > 0 ? false : true;

      if (pa.label.toLowerCase() === "finalize score" && canFinalize) {
        actionIcons.push(
          renderActionIcon(
            pa.id,
            t("labels.check.scoring.actions.finalize"),
            "Finalize",
            disabled,
            "far fa-balance-scale-left",
          ),
        );
      } else if (
        process_flow_state === "Escalated" &&
        pa.label.toLowerCase() === "approve score" &&
        canApprove
      ) {
        actionIcons.push(
          renderActionIcon(
            pa.id,
            t("labels.check.scoring.actions.approve"),
            "Approve",
            disabled,
            "far fa-check-circle",
          ),
        );
      } else if (
        process_flow_state === "Escalated Proceed At Risk" &&
        pa.label.toLowerCase() === "approve proceed at risk" &&
        canApprove
      ) {
        actionIcons.push(
          renderActionIcon(
            pa.id,
            t("labels.check.scoring.actions.approve"),
            "Approve",
            disabled,
            "far fa-check-circle",
          ),
        );
      }
    }
    return actionIcons;
  };

  return (
    <Grid container direction="row" className={classes.container} spacing={0}>
      <Grid
        container
        item
        xs={5}
        direction="column"
        className={classes.borderRight}
        alignItems="stretch"
      >
        <Grid item>
          <Grid container justifyContent="center">
            <Typography component="span" className={classes.textStyle}>
              {t(`labels.checks.${checkType}`)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container justifyContent="space-around">
            {renderActions()}
          </Grid>
        </Grid>
      </Grid>

      <Grid container item xs={7}>
        <Grid
          container
          item
          xs={6}
          direction="column"
          className={classes.borderRight}
          alignItems="stretch"
        >
          <Grid item className={classes.borderBottom}>
            <Grid container justifyContent="center">
              {renderCanWorkWith()}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container justifyContent="center">
              <Grid item>
                {checkRequest &&
                checkRequest.process_flow_state !== "Incomplete"
                  ? renderScore()
                  : "-"}
              </Grid>
              <Grid item>{}</Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={6}
          direction="column"
          alignItems="stretch"
          justifyContent="center"
        >
          <Grid item className={classes.borderBottom}>
            <Grid container justifyContent="center">
              <Typography component="span" className={classes.scoreText}>
                {score && score.valid_from_date
                  ? formatDate(score.valid_from_date)
                  : "-"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container justifyContent="center">
              <Typography component="span" className={classes.scoreText}>
                {score && score.expiry_date
                  ? formatDate(score.expiry_date)
                  : "-"}
              </Typography>
              {score && score.expired && (
                <Typography component="span" className={classes.expiredText}>
                  {t("labels.check.scoring.states.expired")}
                </Typography>
              )}
              {checkRequest &&
                (checkRequest.process_flow_state === "Proceed At Risk" ||
                  checkRequest.process_flow_state ===
                    "Escalated Proceed At Risk") && (
                  <Typography component="span" className={classes.expiredText}>
                    {t(`labels.check.finalization.proceedAtRisk`)}
                  </Typography>
                )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {score && score.approved && (
        <Grid container item xs={12} className={classes.borderTop}>
          <Grid
            container
            item
            xs={5}
            justifyContent="center"
            className={classes.borderRight}
          >
            <Typography component="span" className={classes.textStyle1}>
              {t("labels.check.scoring.completedBy")}
            </Typography>
          </Grid>

          <Grid container item xs={7} justifyContent="center">
            <Typography component="span" className={classes.textStyle1}>
              {score.completed_by_user_name}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(RecordCheckStatus);
